import { useState, useEffect, useRef } from 'react';
import ShakeWrapper from '../shared/ShakeWrapper';
import MultipleChoiceOption from '../shared/MultipleChoiceOption';

export default function McqDefinitionToWord({
  definition,
  pos,
  choices,
  onAnswer,
  // Controlled props for live-session mirroring. When provided, this
  // component reflects them instead of its own internal state, and emits
  // changes back via the setters so the parent (ReviewActivity) can
  // broadcast them to the teacher's observer copy.
  selected: controlledSelected,
  result: controlledResult,
  correctChoiceId: controlledCorrectChoiceId,
  onSelect,
  onResult,
  onCorrectChoiceId,
}) {
  const [localSelected, setLocalSelected] = useState(null);
  const [localResult, setLocalResult] = useState(null);
  const [localCorrectChoiceId, setLocalCorrectChoiceId] = useState(null);
  const selected = controlledSelected !== undefined ? controlledSelected : localSelected;
  const result = controlledResult !== undefined ? controlledResult : localResult;
  const correctChoiceId =
    controlledCorrectChoiceId !== undefined ? controlledCorrectChoiceId : localCorrectChoiceId;
  const setSelected = (v) => (typeof onSelect === 'function' ? onSelect(v) : setLocalSelected(v));
  const setResult = (v) => (typeof onResult === 'function' ? onResult(v) : setLocalResult(v));
  const setCorrectChoiceId = (v) =>
    typeof onCorrectChoiceId === 'function'
      ? onCorrectChoiceId(v)
      : setLocalCorrectChoiceId(v);

  const startTime = useRef(Date.now());
  const shakeRefs = useRef({});

  useEffect(() => {
    startTime.current = Date.now();
    setSelected(null);
    setResult(null);
    setCorrectChoiceId(null);
  }, [definition]);

  const handleChoose = (choiceId) => {
    if (selected) return;
    setSelected(choiceId);

    const elapsed = Date.now() - startTime.current;

    onAnswer({ choice_id: choiceId, elapsed_ms: elapsed }).then(
      (res) => {
        // Strict-true check — the loose `res?.correct !== false` form treated
        // a missing/malformed response (undefined `correct`) as "correct,"
        // which could surface a wrong icon over a correct tile if upstream
        // ever returned an unexpected shape. Require `res.correct === true`
        // so anything non-true reads as wrong rather than "unknown but green."
        const isCorrect = res?.correct === true;
        setResult(isCorrect ? 'correct' : 'wrong');
        if (res?.correct_choice_id) {
          setCorrectChoiceId(res.correct_choice_id);
        }

        if (!isCorrect) {
          shakeRefs.current[choiceId]?.shake();
        }
      },
      // If postAnswer threw (network / non-ok response), surface it to the
      // console but don't flip the tile to 'wrong' — there's no grader verdict
      // to display, so leave the tile in its selected (un-resolved) state.
      (err) => {
        console.error('[McqDefinitionToWord] grading failed:', err);
      }
    );
  };

  return (
    <div className="space-y-8">
      {/* Definition prompt — POS label above, definition constrained to
          max-w-xl so long glosses wrap evenly across two lines instead
          of orphaning a single word on the second line. */}
      <div className="text-center space-y-2">
        {pos && (
          <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium">
            {pos}
          </div>
        )}
        <p className="font-serif text-xl md:text-2xl leading-relaxed text-gray-800 dark:text-gray-200 max-w-xl mx-auto">
          {definition}
        </p>
      </div>

      {/* Choices grid — visual feedback is shared with onboarding Stage 2.5
          via MultipleChoiceOption: green border + Check on correct (whether
          student picked or revealed), red border + X on wrong selection. */}
      <div className="grid grid-cols-2 gap-3">
        {choices.map((choice) => {
          const isSelected = selected === choice.id;
          const isWrong = isSelected && result === 'wrong';
          const isCorrectReveal =
            result === 'wrong' && !isSelected && correctChoiceId === choice.id;

          let state = 'default';
          if (isSelected && result === 'correct') state = 'student_correct';
          else if (isWrong) state = 'student_wrong';
          else if (isCorrectReveal) state = 'reveal_correct';
          else if (selected) state = 'unselected';

          return (
            <ShakeWrapper
              key={choice.id}
              ref={(el) => {
                if (el) shakeRefs.current[choice.id] = el;
              }}
            >
              <MultipleChoiceOption
                state={state}
                layout="grid"
                text={choice.lemma}
                onClick={() => handleChoose(choice.id)}
                disabled={!!selected}
                focusKey={`mcq-def-to-word-${choice.id}`}
              />
            </ShakeWrapper>
          );
        })}
      </div>
    </div>
  );
}
